import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { SITE } from "../data/site";

const NAV = [
  { label: "Services", to: "/services", testid: "nav-services-link" },
  { label: "Locations", to: "/locations", testid: "nav-locations-link" },
  { label: "Resources", to: "/resources", testid: "nav-resources-link" },
  { label: "Contact", to: "/contact", testid: "nav-contact-link" },
  { label: "Coverage", anchor: "coverage", testid: "nav-coverage-link" },
  { label: "FAQ", anchor: "faq", testid: "nav-faq-link" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goAnchor = (anchor) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${anchor}`);
    } else {
      document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 border-b border-white/10 bg-ink/85 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between py-3">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group" onClick={() => setOpen(false)}>
          <span className="w-9 h-9 bg-amber flex items-center justify-center font-display font-black text-ink text-xl leading-none transition-transform duration-300 group-hover:rotate-90">
            X
          </span>
          <span className="leading-none">
            <span className="block font-display font-extrabold uppercase tracking-wider text-white text-lg group-hover:text-amber transition-colors duration-300">
              Xtreme Protection
            </span>
            <span className="block font-mono text-[9px] uppercase tracking-[0.3em] text-amber mt-1">
              England Security Services
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {NAV.map((item) =>
            item.anchor ? (
              <button
                key={item.label}
                data-testid={item.testid}
                onClick={() => goAnchor(item.anchor)}
                className="font-display font-bold uppercase tracking-widest text-sm text-gray-300 hover:text-amber transition-colors duration-300"
              >
                {item.label}
              </button>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                data-testid={item.testid}
                className="font-display font-bold uppercase tracking-widest text-sm text-gray-300 hover:text-amber transition-colors duration-300"
              >
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={SITE.phoneHref}
            data-testid="header-phone-btn"
            className="hidden sm:flex items-center gap-2 bg-amber text-ink font-display font-bold uppercase tracking-widest text-sm px-5 py-2.5 hover:bg-white transition-colors duration-300"
          >
            <Phone className="w-4 h-4" />
            {SITE.phone}
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-white/10 bg-ink/95 backdrop-blur-xl">
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV.map((item) =>
              item.anchor ? (
                <button
                  key={item.label}
                  data-testid={`mobile-${item.testid}`}
                  onClick={() => goAnchor(item.anchor)}
                  className="text-left font-display font-bold uppercase tracking-widest text-base text-gray-200 hover:text-amber py-3 border-b border-white/5 transition-colors"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  data-testid={`mobile-${item.testid}`}
                  onClick={() => setOpen(false)}
                  className="font-display font-bold uppercase tracking-widest text-base text-gray-200 hover:text-amber py-3 border-b border-white/5 transition-colors"
                >
                  {item.label}
                </Link>
              )
            )}
            <a
              href={SITE.phoneHref}
              data-testid="mobile-phone-btn"
              className="mt-3 flex items-center justify-center gap-2 bg-amber text-ink font-display font-bold uppercase tracking-widest text-sm px-5 py-3"
            >
              <Phone className="w-4 h-4" />
              {SITE.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
